import { EventRow } from '../EventRow'
import { Skeleton } from '../../ui'
import type { CardsById } from '../../lib/chat'
import { cardSlots } from '../../lib/chatClient'
import { fmt } from '../../lib/calendar'
import type { ShowingLite } from '../CardBody'
import type { FeedRow } from '../../lib/types'

/** "Fri 2 Oct", and the year only when it is not this one. */
function dayWords(day: string): string {
  const thisYear = day.slice(0, 4) === String(new Date().getFullYear())
  return fmt(day, thisYear
    ? { weekday: 'short', day: 'numeric', month: 'short' }
    : { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).replace(/,/g, '')
}

/**
 * A `cards` part: one `EventRow` per id, in the order the butler sent them.
 *
 * The row is the row everywhere else (E4) — same component, same width,
 * flush in the column, no inset and no card around it — so a tap opens the
 * same `EventSheet` and a swipe saves or dismisses exactly as it does on
 * Browse. A row still being fetched draws as a skeleton.
 *
 * a2-05 — a row in neither the feed cache nor `feed_lite` has gone (it ran,
 * or was cancelled). Doug chose: keep its title and date, struck through,
 * from what the Worker stored beside the ids (`titles`, `firsts`), so a
 * thread reopened later still reads. A message stored before titles were
 * has nothing to draw, and its gone cards fold into one line
 * (`cardSlots`).
 */
export function Cards({ ids, titles, firsts, rows, byEntry, onOpen }: {
  ids: string[]
  /** Stored by the Worker beside the ids; absent on a live turn. */
  titles?: Record<string, string>
  firsts?: Record<string, string>
  rows: CardsById
  byEntry: Map<string, ShowingLite[]> | undefined
  onOpen: (e: FeedRow) => void
}) {
  if (rows.pending) {
    return (
      <div className="chat-cards">
        {ids.map((id, i) => {
          const e = rows.get(id)
          return e
            ? <EventRow key={`${id}-${i}`} e={e} showings={byEntry?.get(e.id)} onOpen={onOpen} />
            : <Skeleton key={`${id}-${i}`} variant="row" />
        })}
      </div>
    )
  }
  return (
    <div className="chat-cards">
      {cardSlots(ids, id => rows.has(id), titles, firsts).map((s, i) => {
        if (s.kind === 'row') {
          const e = rows.get(s.id)!
          return <EventRow key={`${s.id}-${i}`} e={e} showings={byEntry?.get(e.id)} onOpen={onOpen} />
        }
        if (s.kind === 'gone') {
          return (
            <p key={`${s.id}-${i}`} className="chat-gone">
              <del>{s.title}</del>
              {s.day && <>{' · '}<del>{dayWords(s.day)}</del></>}
              {/* A strike is not read aloud; this is. */}
              <span className="u-sr"> — no longer listed</span>
            </p>
          )
        }
        return (
          <p key={`passed-${i}`} className="chat-gone">
            {s.count === 1 ? '1 event has passed' : `${s.count} events have passed`}
          </p>
        )
      })}
    </div>
  )
}
